import { Box, Button, CloseButton, Group, Image, Loader, LoadingOverlay, Modal, Overlay, Portal, ScrollArea, Select, SimpleGrid, Stack, Table, Text } from "@mantine/core"
import { useForm } from "@mantine/form"
import { yupResolver } from "mantine-form-yup-resolver"
import { CreditCardIcon } from "@phosphor-icons/react"
import React, { ReactNode, useEffect, useState } from "react"
import { useSelector } from "react-redux"
import useSwr, { mutate as globalMutate } from "swr"
import * as yup from "yup"
import { useTranslation } from "react-i18next"
import { i18n } from "@dehub/theme"
import { InvoiceApi, OrderApi } from "../../apis"
import { IGeneral, IReference } from "../../store/General"
import { currencyFormat } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { DotPill } from "../delivery/atoms"
import { usePaymentPolling } from "./use-payment-polling"
import classes from "./payment-form.module.css"

const schema = yup.object({
  method: yup.string().required(() => i18n.t("common:validation.required")).nullable(),
})

export type IFormData = {
  name: string
  amount: string
  addInfo: string
  method: string
}

type Props = {
  action: any
  setAction: any
  reload: any
}

/** Methods the buyer cannot pay an invoice with from here. */
const HIDDEN_METHODS = ["CASH", "BANK_CARD", "SOCIAL_PAY", "BANK_ACCOUNT"]

function Card({ title, children, flush }: Readonly<{ title?: ReactNode; children: ReactNode; flush?: boolean }>) {
  return (
    <Box className={classes.card} data-flush={flush || undefined}>
      {title && <Text className={classes.cardTitle}>{title}</Text>}
      {children}
    </Box>
  )
}

function Row({ label, value, mono }: Readonly<{ label: ReactNode; value?: ReactNode; mono?: boolean }>) {
  const empty = value === undefined || value === null || value === ""
  return (
    <Group justify="space-between" wrap="nowrap" gap="md">
      <Text className={classes.kvLabel}>{label}</Text>
      <Text className={mono ? classes.kvMono : classes.kvValue} ta="right" truncate="end">
        {empty ? "-" : value}
      </Text>
    </Group>
  )
}

/**
 * Buyer pays a confirmed order's invoice (canvas OrdB_OrderDetail «Нэхэмжлэх» → «Төлбөр
 * баталгаажуулах», QPay state from OrdB_Checkout). Left: who bills whom and for what;
 * right: the amount and the one action.
 */
export function BuyerSalesPaymentForm({ action, setAction, reload }: Props) {
  const { t } = useTranslation(["order", "common"])
  const [opened, setOpened] = React.useState<string[]>([])
  const [loading, setLoading] = React.useState<boolean>(false)
  const [qrCode, setQrCode] = React.useState<any>()
  const { paymentMethod, bankAccounts } = useSelector((state: { general: IGeneral }) => state.general)
  const [invoiceData, setInvoiceData] = useState(Object.create({}))
  const poll = usePaymentPolling()

  const form = useForm<IFormData>({
    mode: "controlled",
    initialValues: { name: "", amount: "", addInfo: "", method: "" },
    validate: yupResolver(schema),
  })

  useEffect(() => {
    const fetchInvoiceData = async () => {
      if (!action || !action[1]?.proformaInvoiceId) return

      setLoading(true)

      try {
        const data = await InvoiceApi.get(action[1].proformaInvoiceId)
        setInvoiceData(data)
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
      } finally {
        setLoading(false)
      }
    }

    fetchInvoiceData()
  }, [action])

  const { data: orderData } = useSwr(`/inv/invoice/order`, async () => await OrderApi.get(action && action[1]?.id))

  const payPayload = (method: string) => ({
    invoiceId: invoiceData?.id,
    invoiceRefCode: invoiceData?.refCode,
    receiverBusinessId: invoiceData?.senderBusiness?.id,
    method,
    amount: invoiceData?.amountToPay,
    description: invoiceData?.refCode,
    creditAccountId: invoiceData?.receiverAcc?.id,
    creditAccountBank: invoiceData?.receiverAcc?.bankName,
    creditAccountName: invoiceData?.receiverAcc?.name,
    creditAccountNumber: invoiceData?.receiverAcc?.number,
    creditAccountCurrency: invoiceData?.receiverAcc?.currency,
    debitAccountId: invoiceData?.senderAcc?.id,
    debitAccountBank: invoiceData?.senderAcc?.bankName,
    debitAccountName: invoiceData?.senderAcc?.name,
    debitAccountNumber: invoiceData?.senderAcc?.number,
    debitAccountCurrency: invoiceData?.senderAcc?.currency,
    addInfo: "",
    files: [],
  })

  const onSubmit = async (data: IFormData) => {
    setLoading(true)
    if (data?.method === "QPAY") {
      try {
        const res = await InvoiceApi.pay(payPayload(data.method))

        if (!res?.qr_image) {
          message.error((res as any)?.message || t("order:buyerSales.paymentPendingTimeout"))
          setLoading(false)
          return
        }

        setOpened(["image"])
        setQrCode(res?.qr_image)

        // QPAY has no popup window — the buyer scans the QR with their bank app.
        // Poll the invoice the same way as Golomt until the de-payment-service
        // QPAY confirmation flips it to paid, then close the QR + refresh.
        poll.start({
          invoiceId: invoiceData?.id,
          popup: null,
          onPaid: () => {
            message.success(t("order:buyerSales.paymentSuccess"))
            globalMutate(`/inv/invoice/${invoiceData?.id}`)
            reload()
            setOpened([])
            setAction([])
          },
          onPending: () => {
            message.error(t("order:buyerSales.paymentPendingTimeout"))
            globalMutate(`/inv/invoice/${invoiceData?.id}`)
            reload()
          },
        })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
      }
    } else {
      try {
        const res = await InvoiceApi.pay(payPayload(data.method))
        if (!res?.url) {
          message.error(t("order:buyerSales.popupBlocked"))
          setLoading(false)
          return
        }

        const popup = window.open(
          res.url,
          "golomt-payment",
          "toolbar=yes,scrollbars=yes,resizable=yes,width=800,height=600",
        )

        if (!popup) {
          message.error(t("order:buyerSales.popupBlocked"))
          setLoading(false)
          return
        }

        // The bank popup stays on Golomt's own domain, so we cannot postMessage
        // back. Instead poll the invoice status until the de-payment-service
        // background confirmation flips it to paid, then close + refresh.
        poll.start({
          invoiceId: invoiceData?.id,
          popup,
          onPaid: () => {
            message.success(t("order:buyerSales.paymentSuccess"))
            globalMutate(`/inv/invoice/${invoiceData?.id}`)
            reload()
            setAction([])
          },
          onPending: () => {
            message.error(t("order:buyerSales.paymentPendingTimeout"))
            globalMutate(`/inv/invoice/${invoiceData?.id}`)
            reload()
          },
        })
      } catch (err) {
        message.error((err as HttpHandler)?.message!)
      }
    }

    setLoading(false)
  }

  const onCloseQr = () => {
    setOpened([])
    // One last status check before giving up; a manual close is not a failure.
    poll.finalize()
  }

  const senderBusiness = invoiceData?.senderBusiness
  const receiverBusiness = invoiceData?.receiverBusiness
  const lines: any[] = orderData?.lines || []
  const amountToPay = Number(invoiceData?.amountToPay) || 0
  const totalAmount = Number(orderData?.totalAmount) || 0
  const isAdvance = amountToPay > 0 && totalAmount > 0 && amountToPay < totalAmount

  const methods = (paymentMethod || [])
    .filter((c: IReference) => !HIDDEN_METHODS.includes(c.code))
    .map((item: IReference) => ({ label: item.name, value: item.code }))

  return (
    <>
      {/* Full-screen block while the Golomt popup is open and we poll for confirmation:
          cover the whole admin so no other action can run until the payment settles,
          times out, or the popup closes. NOT for the QPAY/QR flow (opened[0] ===
          "image") — that shows the QR in its own modal the user must keep scanning. */}
      {poll.polling && opened[0] !== "image" && (
        <Portal>
          <Overlay fixed zIndex={3000} backgroundOpacity={0.6} blur={2}>
            <Stack align="center" justify="center" h="100%" gap="md">
              <Loader size="lg" color="white" />
              <Text c="white" fw={600}>{t("order:buyerSales.paymentProcessing")}</Text>
              <Text c="gray.3" size="sm" ta="center" maw={360}>{t("order:buyerSales.paymentPollingNote")}</Text>
            </Stack>
          </Overlay>
        </Portal>
      )}

      <Box className={classes.root}>
        <LoadingOverlay visible={loading} />

        <Group justify="space-between" wrap="nowrap" gap="md" className={classes.header}>
          <Box miw={0}>
            <Text className={classes.title}>{t("order:buyerSales.paymentConfirm")}</Text>
            <Group gap="sm" mt={4} wrap="wrap">
              <Text className={classes.mono} translate="no">{invoiceData?.refCode || "-"}</Text>
              {invoiceData?.respondText && (
                <Text className={classes.meta}>
                  {t("order:buyerSales.orderNo")} <Text span className={classes.mono} translate="no">{invoiceData.respondText}</Text>
                </Text>
              )}
              <DotPill tone="amber">{t("order:buyerSales.paymentPending")}</DotPill>
            </Group>
          </Box>
          <CloseButton size="lg" onClick={() => setAction([])} />
        </Group>

        <ScrollArea.Autosize mah="calc(100dvh - 12rem)" type="auto">
          <Box className={classes.body}>
            {/* ── Invoice ── */}
            <Stack gap="md" miw={0}>
              <Card title={t("order:buyerSales.invoiceTitle")}>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xl" verticalSpacing="md">
                  <Stack gap={6} miw={0}>
                    <Text className={classes.groupLabel}>{t("order:buyerSales.invoiceSender")}</Text>
                    <Text className={classes.partyName}>{renderBusinessName(senderBusiness?.partner) || renderBusinessName(senderBusiness)}</Text>
                    <Row label={t("order:buyerSales.taxId")} value={senderBusiness?.regNumber} mono />
                    <Row label={t("order:buyerSales.address")} value={senderBusiness?.partner?.businessAddress} />
                    <Row label={t("order:buyerSales.phone")} value={senderBusiness?.partner?.phone} mono />
                    <Row label={t("order:buyerSales.email")} value={senderBusiness?.partner?.email} />
                    <Row label={t("order:buyerSales.bankName")} value={invoiceData?.senderAcc?.bankName} />
                    <Row label={t("order:buyerSales.bankAccountNo")} value={invoiceData?.senderAcc?.number} mono />
                  </Stack>
                  <Stack gap={6} miw={0}>
                    <Text className={classes.groupLabel}>{t("order:buyerSales.invoiceReceiver")}</Text>
                    <Text className={classes.partyName}>{renderBusinessName(receiverBusiness?.partner) || renderBusinessName(receiverBusiness)}</Text>
                    <Row label={t("order:buyerSales.taxId")} value={receiverBusiness?.regNumber} mono />
                    <Row label={t("order:buyerSales.address")} value={receiverBusiness?.partner?.businessAddress} />
                    <Row label={t("order:buyerSales.phone")} value={receiverBusiness?.partner?.phone} mono />
                    <Row label={t("order:buyerSales.date")} value={invoiceData?.createdAt ? dateTimeSecFormat(invoiceData.createdAt) : undefined} mono />
                    <Row label={t("order:buyerSales.paymentDue")} value={invoiceData?.paymentDate ? dateTimeSecFormat(invoiceData.paymentDate) : undefined} mono />
                  </Stack>
                </SimpleGrid>
              </Card>

              <Card title={t("order:buyerSales.products")} flush>
                <Table className={classes.table} verticalSpacing={0} horizontalSpacing={0}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("order:buyerOrders.detail.item")}</Table.Th>
                      <Table.Th className={classes.right}>{t("order:buyerOrders.detail.qtyPrice")}</Table.Th>
                      <Table.Th className={classes.right}>{t("order:buyerOrders.detail.amount")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {lines.map((line: any, index: number) => (
                      <Table.Tr key={line?.id || index}>
                        <Table.Td>
                          <Text className={classes.lineName}>{line?.name || "-"}</Text>
                          <Text className={classes.mono} c="var(--nw-text-2)" translate="no">
                            {[line?.skuCode, line?.unit].filter(Boolean).join(" · ") || "-"}
                          </Text>
                        </Table.Td>
                        <Table.Td className={`${classes.mono} ${classes.right}`}>
                          {`${Number(line?.quantity) || 0} x ${currencyFormat(Number(line?.price) || 0)}`}
                        </Table.Td>
                        <Table.Td className={`${classes.mono} ${classes.right}`}>
                          {currencyFormat((Number(line?.quantity) || 0) * (Number(line?.price) || 0))}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                    {lines.length === 0 && (
                      <Table.Tr>
                        <Table.Td colSpan={3} ta="center" c="var(--nw-text-2)">
                          {t("order:buyerSales.noProducts")}
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </Card>
            </Stack>

            {/* ── Pay ── */}
            <Box className={classes.side}>
              <Card title={t("order:buyerSales.payment")}>
                <Stack gap={6}>
                  <Row label={t("order:buyerSales.goodsServicePrice")} value={currencyFormat(Number(orderData?.orderAmount) || 0)} mono />
                  <Row label={t("order:buyerOrders.detail.vatIncluded")} value={currencyFormat(Number(orderData?.lineVatAmount) || 0)} mono />
                  {Number(orderData?.lineTaxAmount) > 0 && (
                    <Row label={t("order:buyerSales.cityTax")} value={currencyFormat(Number(orderData?.lineTaxAmount))} mono />
                  )}
                  <Row label={t("order:buyerSales.grandTotal")} value={currencyFormat(totalAmount)} mono />
                  <Box className={classes.totalRow}>
                    <Text fw={600}>{isAdvance ? t("order:buyerSales.advancePayment") : t("order:buyerSales.amountToPay")}</Text>
                    <Text className={classes.grand}>{currencyFormat(amountToPay)}</Text>
                  </Box>
                </Stack>

                <Stack gap="md" mt="lg">
                  <Select
                    {...form.getInputProps("method")}
                    label={t("order:buyerSales.selectPaymentMethod")}
                    placeholder={t("order:buyerSales.paymentMethodPlaceholder")}
                    data={methods}
                    required
                  />

                  {form.values.method === "B2B" && (
                    <Select
                      {...form.getInputProps("name")}
                      label={t("order:buyerSales.selectPaymentAccount")}
                      data={(bankAccounts || []).map((item: any) => ({
                        label: `${item.bankName} / ${item.number}`,
                        value: item.id,
                        disabled: !item.isEnabled,
                      }))}
                      placeholder={t("order:buyerSales.businessAccountPlaceholder")}
                      required
                    />
                  )}

                  <Box>
                    <Row label={t("order:buyerSales.transactionDescription")} value={invoiceData?.refCode} mono />
                    <Text className={classes.hint} mt={4}>{t("order:buyerSales.transferHint")}</Text>
                  </Box>

                  <Button
                    fullWidth
                    size="md"
                    color="brand"
                    leftSection={<CreditCardIcon size={18} />}
                    loading={loading || poll.polling}
                    disabled={invoiceData?.invoiceStatus !== "CONFIRMED" || loading || poll.polling}
                    onClick={() => {
                      const { hasErrors } = form.validate()
                      if (!hasErrors) onSubmit(form.values)
                    }}>
                    {poll.polling ? t("order:buyerSales.paymentProcessing") : t("order:buyerSales.confirmPayment")}
                  </Button>

                  {poll.polling && (
                    <Text className={classes.hint} ta="center">
                      {t("order:buyerSales.paymentPollingNote")}
                    </Text>
                  )}
                </Stack>
              </Card>
            </Box>
          </Box>
        </ScrollArea.Autosize>
      </Box>

      {/* QPay QR (canvas OrdB_Checkout, QPay state) */}
      <Modal opened={opened[0] === "image"} onClose={onCloseQr} withCloseButton={false} centered radius="lg" size={640} padding="lg">
        <Group justify="space-between" wrap="nowrap" mb="md">
          <Text className={classes.cardTitle} mb={0}>{t("order:buyerSales.qpayTitle")}</Text>
          <CloseButton onClick={onCloseQr} />
        </Group>
        <Box className={classes.qr}>
          <Box className={classes.qrImage}>
            <Image src={"data:image/png;base64," + qrCode} alt="QPay QR" w={188} h={188} />
          </Box>
          <Stack gap="sm" miw={0} flex={1}>
            <Row label={t("order:buyerSales.invoiceNo")} value={invoiceData?.refCode} mono />
            <Row label={t("order:buyerSales.amountToPay")} value={currencyFormat(amountToPay)} mono />
            {poll.polling && (
              <Group gap="xs" wrap="nowrap">
                <Loader size="xs" />
                <Text fw={600} fz="var(--nw-fz-body)">{t("order:buyerSales.paymentProcessing")}</Text>
              </Group>
            )}
            <Text className={classes.hint}>{t("order:buyerSales.qrScanNote")}</Text>
          </Stack>
        </Box>
      </Modal>
    </>
  )
}
